import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { sendErrorEvent } from '@trello/error-reporting';
import { Spinner } from '@trello/nachos/spinner';
import type { PIIString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import { makeTemplateUrlSlug } from '@trello/urls';

import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import { useTemplatesStoryQuery } from './TemplatesStoryQuery.generated';
import { TemplateStoryContent } from './TemplateStoryContent';
import { TemplateStoryError } from './TemplateStoryError';
import type { Template } from './types';

import styles from './TemplateStory.less';

interface Classes {
  storyContainer: string;
}

const startTask = () => {
  return Analytics.startTask({
    taskName: 'view-template/story',
    source: 'templateStoryPageScreen',
  });
};

const defaultClasses = { storyContainer: '' };
export const TemplateStory: FunctionComponent<{
  category: string;
  shortLink: string;
  classes?: Classes;
  isInAppGallery: boolean;
  username?: PIIString;
  templates: Template[];
}> = ({
  category,
  shortLink,
  classes = defaultClasses,
  isInAppGallery,
  username,
  templates,
}) => {
  const [taskId, setTaskId] = useState('');
  const taskCompletedRef = useRef(false);

  useEffect(() => {
    const newTaskId = startTask();
    setTaskId(newTaskId);
    return () => {
      if (!taskCompletedRef.current && newTaskId) {
        Analytics.taskAborted({
          taskName: 'view-template/story',
          source: 'templateStoryPageScreen',
          traceId: newTaskId,
        });
      }
    };
  }, []);

  useEffect(() => {
    const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
    Analytics.sendMarketingScreenEvent({
      url,
      referrerUrl,
      referrerScreenName,
      event: {
        source: 'templateStoryPageScreen',
        name: 'templateStoryPageScreenV2',
        attributes: {
          category,
          template: shortLink,
        },
      },
    });
  });

  const { data, error, loading } = useTemplatesStoryQuery({
    variables: { boardId: shortLink, count: 3, traceId: taskId },
    waitOn: ['CurrentBoardInfo'],
  });

  const onErrorMount = useCallback(() => {
    if (!taskId) return;
    if (!error) return;

    sendErrorEvent(new Error(error.message), {
      tags: {
        ownershipArea: 'trello-web-eng',
      },
      extraData: {
        networkError: error.networkError ? error.networkError.message : false,
        extraInfo: error.extraInfo,
        graphQLErrors: error.graphQLErrors.toString(),
        stack: error.stack ? error.stack : false,
        name: error.name,
      },
    });

    Analytics.taskFailed({
      taskName: 'view-template/story',
      traceId: taskId,
      source: 'templateStoryPageScreen',
      error,
    });

    taskCompletedRef.current = true;
  }, [error, taskId]);

  const onContentMount = useCallback(() => {
    if (!taskId) return;

    Analytics.taskSucceeded({
      taskName: 'view-template/story',
      source: 'templateStoryPageScreen',
      traceId: taskId,
    });

    taskCompletedRef.current = true;
  }, [taskId]);

  // the category in the url path doesn't have to match the actual category for the page to load
  // if there is a mismatch between url category and actual template category, we redirect
  if (
    data?.board?.name &&
    data.board.templateGallery &&
    data.board?.templateGallery?.category !== category
  ) {
    const { name, templateGallery } = data.board;

    navigate(
      `/templates/${makeTemplateUrlSlug(
        templateGallery.category,
        name,
        shortLink,
      )}`,
      {
        replace: true,
        trigger: true,
      },
    );

    Analytics.taskAborted({
      taskName: 'view-template/story',
      source: 'templateStoryPageScreen',
      traceId: taskId,
    });

    taskCompletedRef.current = true;
    return null;
  }

  const board = data?.board ? data.board : null;

  if (error) {
    return <TemplateStoryError onMount={onErrorMount} />;
  }

  return (
    <div
      className={classNames(styles.storyContainer, {
        [classes.storyContainer]: !!classes.storyContainer,
      })}
    >
      {loading ? (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      ) : (
        <TemplateStoryContent
          category={category}
          board={board}
          shortLink={shortLink}
          isInAppGallery={isInAppGallery}
          username={username}
          templates={templates}
          onMount={onContentMount}
        />
      )}
    </div>
  );
};
